import React, { useMemo, useCallback } from 'react';
import DeltaArrowIcon from '../icons/DeltaArrowIcon';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';

import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	getWorkOrderBasePriorityBackgroundColor,
	getWorkOrderBasePriorityPillBackgroundColor,
} from '../../utils/DataFormatterUtils';
import { buildLifeCyclePriorityTableColumns } from './LifeCyclePriorityTableColumns';
import { buildLifeCycleSupplierTableColumns } from './LifeCycleSupplierTableColumns';
import {
	MONTH_ABBR,
	MONTH_FULL,
	CHART_METRIC_OPTIONS,
	VIEW_MODE,
	CHART_PRIMARY_COLOR,
	CHART_METRIC,
	COMPARISON_CHART_KEYS,
	PERFORMER_GROUP,
	PERFORMER_SHAPE,
	NEGATIVE_CHANGE_COLOR,
	POSITIVE_CHANGE_COLOR,
} from './LifeCycleAnalyticsConstants';
import {
	formatSupplierName,
	toMinutes,
	formatDurationShort,
	buildSpendTooltipHtml,
	buildSupplierTooltipHtml,
	parseSafeInt,
	parseSafeFloat,
	extractSpendValue,
	roundToOneDecimalPlace,
} from './LifeCycleAnalyticsUtils';
import { COMPARISON_MODE, resolveComparisonRange } from '../../utils/AnalyticsComparisonUtils';
import moment from 'moment';

/** Derived tables, chart series, CSV exports, and dashboard props from useLifeCycleAnalyticsData. */
export function useLifeCycleAnalyticsPresentation(data: any) {
	const {
		companyConfig,
		currentUser,
		userType,
		problemTypes,
		fetchProblemTypes,
		history,
		loading,
		chartLoading,
		prioritiesLoading,
		error,
		prioritySummary,
		supplierPerformance,
		allMonthlyMetricData,
		monthlySpend,
		chartComparisons,
		comparisonData,
		comparisonLoading,
		setComparisonForChart,
		activeFilters,
		draftFilters,
		filterModalVisible,
		selectedPriorityId,
		minWoCount,
		setMinWoCount,
		viewMode,
		createdAtPickerDates,
		completedAtPickerDates,
		isAutoApprovalEnabled,
		priorityMap,
		handleArrowClick,
		handleCardClick,
		navigateToSupplierDetail,
		openFilterModal,
		applyFilters,
		cancelFilters,
		removeProblemTypeFilter,
		removeIsPMFilter,
		handleRemoveCreatedAtFilter,
		handleRemoveCompletedAtFilter,
		handleProblemTypeChange,
		handleCreatedAtCalendarChange,
		handleCreatedAtChange,
		handleCreatedAtOpenChange,
		handleCompletedAtCalendarChange,
		handleCompletedAtChange,
		handleCompletedAtOpenChange,
		handleIsPMChange,
	} = data;

	const chartColor =
		nullSafeGet('detail.config.theme.navblock', companyConfig) || CHART_PRIMARY_COLOR;

	const currency = useMemo(() => getCurrency({ currentUser }), [currentUser]);

	const renderSpend = useCallback(
		(value) => (
			<span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'baseline' }}>
				<span>{currency.symbolFormat(value, 0)}</span>
				<span className="footnoteDescription">{currency.id}</span>
			</span>
		),
		[currency]
	);

	const hasActiveFilters =
		activeFilters.problemTypeIds.length > 0 ||
		activeFilters.createdAt !== null ||
		activeFilters.completedAt !== null ||
		activeFilters.isPM !== undefined;

	// Comparison requires BOTH endpoints of completedAt — the data layer's reset effect
	// uses the same two-endpoint check. The filter UI allows partial ranges (e.g.,
	// [start, null]), and a partial range can't resolve a comparison window.
	const hasFullCompletedAtRange = !!(
		activeFilters &&
		activeFilters.completedAt &&
		activeFilters.completedAt[0] &&
		activeFilters.completedAt[1]
	);
	const comparisonDisabledReasonText =
		'Set a Completed between date range filter to enable comparison.';

	const problemTypeNames = useMemo(() => {
		return activeFilters.problemTypeIds
			.map((id) => nullSafeGetOrElse('name', problemTypes.records[id], null))
			.filter(Boolean)
			.join(', ');
	}, [activeFilters.problemTypeIds, problemTypes.records]);

	const totalCompletedWorkOrders = useMemo(() => {
		return prioritySummary.reduce((sum, item) => sum + (item.completedWoCount || 0), 0);
	}, [prioritySummary]);

	const prioritySummaryCards = useMemo(() => {
		const sorted = prioritySummary.slice().sort((a, b) => a.woPriorityId - b.woPriorityId);
		return sorted.map((item) => {
			const completedWoCount = parseSafeInt(item.completedWoCount);
			const avgApprovalTimeSeconds = parseSafeFloat(item.avgApprovalTimeSeconds);
			const avgResponseTimeSeconds = parseSafeFloat(item.avgResponseTimeSeconds);
			const avgResolutionTimeSeconds = parseSafeFloat(item.avgResolutionTimeSeconds);
			const firstTimeFixRate = parseSafeFloat(item.firstTimeFixRate);
			const share =
				totalCompletedWorkOrders > 0
					? roundToOneDecimalPlace((completedWoCount * 100) / totalCompletedWorkOrders)
					: 0;
			const priority = priorityMap[item.woPriorityId] || { id: Number(item.woPriorityId) };
			const priorityColor = getWorkOrderBasePriorityBackgroundColor(priority);
			return {
				...item,
				completedWoCount,
				avgApprovalTimeSeconds,
				avgResponseTimeSeconds,
				avgResolutionTimeSeconds,
				firstTimeFixRate,
				color: typeof priorityColor === 'string' ? priorityColor : priorityColor.string(),
				bgColor: getWorkOrderBasePriorityPillBackgroundColor(priority),
				share,
			};
		});
	}, [prioritySummary, totalCompletedWorkOrders, priorityMap]);

	const selectedPriorityCard = useMemo(
		() => prioritySummaryCards.find((c) => c.woPriorityId === selectedPriorityId) || null,
		[prioritySummaryCards, selectedPriorityId]
	);

	const tableDataSource = useMemo(() => {
		if (viewMode === VIEW_MODE.ALL) {
			return prioritySummaryCards;
		}
		if (!selectedPriorityCard) {
			return prioritySummaryCards;
		}
		return [selectedPriorityCard];
	}, [prioritySummaryCards, selectedPriorityCard, viewMode]);

	const priorityTableColumns = useMemo(
		() =>
			buildLifeCyclePriorityTableColumns({
				selectedPriorityId,
				viewMode,
				handleArrowClick,
				renderSpend,
			}),
		[selectedPriorityId, viewMode, handleArrowClick, renderSpend]
	);

	const availableMetricOptions = useMemo(() => {
		if (isAutoApprovalEnabled) {
			return CHART_METRIC_OPTIONS.filter((m) => m.key !== CHART_METRIC.APPROVAL_TIME);
		}
		return CHART_METRIC_OPTIONS;
	}, [isAutoApprovalEnabled]);

	const buildMonthlyMetricSeries = (rawData: any[], metricConfig: any) => {
		const field = metricConfig.dataField;
		const isTime = metricConfig.isTimeBased;
		return rawData
			.filter((item: any) => !!item && !item.isPreviousPeriod && !item.isPreviousYear)
			.sort((a: any, b: any) => (a.year !== b.year ? a.year - b.year : a.month - b.month))
			.map((item: any) => {
				const val = parseSafeFloat(item[field], NaN);
				const hasValue = !isNaN(val);
				return {
					monthLabel: `${MONTH_ABBR[item.month - 1]} '${String(item.year).slice(2)}`,
					monthFull: `${MONTH_FULL[item.month - 1]} ${item.year}`,
					// MetricChart's date-based comparison pairing depends on monthKey;
					// emit it in the same `YYYY-M` format the spend series uses.
					monthKey: `${item.year}-${item.month}`,
					metricValue: hasValue ? (isTime ? toMinutes(val) : val) : null,
					metricRaw: hasValue ? val : null,
				};
			});
	};

	const metricChartPropsList = useMemo(() => {
		return availableMetricOptions.map((metricConfig) => {
			const rawData = allMonthlyMetricData[metricConfig.key] || [];
			const isTime = metricConfig.isTimeBased;
			const field = metricConfig.dataField;

			// Chart data
			const chartData = buildMonthlyMetricSeries(rawData, metricConfig);
			const previousRaw = (comparisonData && comparisonData[metricConfig.key]) || [];
			const previousChartData = previousRaw.length
				? buildMonthlyMetricSeries(previousRaw, metricConfig)
				: [];
			const comparisonStateForChart =
				(chartComparisons && chartComparisons[metricConfig.key]) || null;
			const selectedRange = hasFullCompletedAtRange
				? [activeFilters.completedAt[0], activeFilters.completedAt[1]]
				: null;
			const comparisonRangeForChart = selectedRange
				? resolveComparisonRange(selectedRange[0], selectedRange[1], comparisonStateForChart)
				: null;

			// Summary value
			let summaryValue;
			if (selectedPriorityCard) {
				summaryValue = parseSafeFloat(selectedPriorityCard[field]);
			} else {
				const valid = rawData
					.filter((item) => item && !item.isPreviousPeriod && !item.isPreviousYear)
					.map((item) => {
						const val = parseSafeFloat(item[field], NaN);
						if (isNaN(val)) return null;
						return val;
					})
					.filter((v) => v !== null);
				if (valid.length === 0) {
					summaryValue = 0;
				} else {
					const avg = valid.reduce((sum, v) => sum + v, 0) / valid.length;
					summaryValue = isTime ? Math.round(avg) : roundToOneDecimalPlace(avg);
				}
			}

			// Delta element
			const changeVal = selectedPriorityCard
				? parseFloat(
						nullSafeGetOrElse(
							metricConfig.changeField,
							nullSafeGetOrElse('changes', selectedPriorityCard, {}),
							''
						)
				  )
				: NaN;
			const hasChange = !isNaN(changeVal) && changeVal !== 0;
			let deltaElement = null;
			if (hasChange) {
				const isPositive = changeVal > 0;
				const color = isPositive !== isTime ? NEGATIVE_CHANGE_COLOR : POSITIVE_CHANGE_COLOR;
				deltaElement = (
					<div style={{ color }} className="flex items-center gap-1">
						<DeltaArrowIcon direction={isPositive ? 'up' : 'down'} color={color} />
						<span className="footnoteMedium">{roundToOneDecimalPlace(Math.abs(changeVal))}%</span>
					</div>
				);
			}

			// Format axis label
			const formatAxisLabel = (val) => {
				const num = parseFloat(String(val));
				if (isNaN(num)) return `${val}`;
				if (isTime) return formatDurationShort(num * 60);
				return `${roundToOneDecimalPlace(num)}%`;
			};

			// Export CSV
			const exportCsv = () => {
				if (chartData.length === 0) return;
				const priorityLabel = selectedPriorityCard ? selectedPriorityCard.woPriorityName : 'All';
				const metricLabel = metricConfig.label.replace(/\s+/g, '_');
				const hasComparison =
					!!comparisonStateForChart &&
					comparisonStateForChart.mode !== COMPARISON_MODE.NONE &&
					previousChartData.length > 0;
				const baseHeaders = isTime
					? ['Month', metricConfig.label + ' (minutes)', metricConfig.label]
					: ['Month', metricConfig.label];
				const headers = hasComparison ? [...baseHeaders, 'Historical Data'] : baseHeaders;
				const csvExporter = new ExportToCsv({
					...CSV_EXPORT_DEFAULTS,
					filename: metricLabel + '_' + priorityLabel.replace(/\s+/g, '_'),
					headers,
				});
				const buildRow = (row, isHistorical) => {
					const isMissing = row.metricRaw === null || row.metricValue === null;
					const base = isTime
						? {
								month: row.monthFull,
								metricMinutes: isMissing ? '' : Math.round(row.metricValue),
								metricFormatted: isMissing ? '' : formatDurationShort(row.metricRaw),
						  }
						: {
								month: row.monthFull,
								metricValue: isMissing ? '' : roundToOneDecimalPlace(row.metricRaw),
						  };
					return hasComparison ? { ...base, historicalData: isHistorical ? 'Yes' : 'No' } : base;
				};
				const currentRows = chartData.map((row) => buildRow(row, false));
				const previousRows = hasComparison
					? previousChartData.map((row) => buildRow(row, true))
					: [];
				csvExporter.generateCsv([...previousRows, ...currentRows]);
			};

			return {
				key: metricConfig.key,
				chartColor: chartColor,
				chartMetric: metricConfig.key,
				activeMetricConfig: metricConfig,
				chartMetricSummaryValue: summaryValue,
				chartMetricDeltaElement: deltaElement,
				monthlyMetricChartData: chartData,
				previousPeriodChartData: previousChartData,
				comparisonState: comparisonStateForChart,
				onComparisonChange: setComparisonForChart
					? (next: any) => setComparisonForChart(metricConfig.key, next)
					: undefined,
				selectedRange: selectedRange,
				comparisonRange: comparisonRangeForChart,
				comparisonLoading: !!(comparisonLoading && comparisonLoading[metricConfig.key]),
				comparisonDisabled: !hasFullCompletedAtRange,
				comparisonDisabledReason: !hasFullCompletedAtRange
					? comparisonDisabledReasonText
					: undefined,
				selectedPriorityCard,
				formatMetricAxisLabel: formatAxisLabel,
				exportMetricChartCsv: exportCsv,
			};
		});
	}, [
		availableMetricOptions,
		allMonthlyMetricData,
		comparisonData,
		chartComparisons,
		comparisonLoading,
		setComparisonForChart,
		activeFilters,
		hasFullCompletedAtRange,
		selectedPriorityCard,
		chartColor,
	]);

	const previousSpendChartData = useMemo(() => {
		const raw = (comparisonData && comparisonData[COMPARISON_CHART_KEYS.SPEND]) || [];
		if (!raw || raw.length === 0) return [];
		return raw
			.filter((item: any) => item && item.year && item.month && !item.isPreviousYear)
			.sort((a: any, b: any) => (a.year !== b.year ? a.year - b.year : a.month - b.month))
			.map((item: any) => {
				const spendValue = extractSpendValue(item);
				return {
					monthLabel: `${MONTH_ABBR[item.month - 1]} '${String(item.year).slice(2)}`,
					monthFull: `${MONTH_FULL[item.month - 1]} ${item.year}`,
					monthKey: `${item.year}-${item.month}`,
					year: String(item.year),
					spendValue,
				};
			});
	}, [comparisonData]);

	const spendChartData = useMemo(() => {
		return monthlySpend
			.filter((item) => item && item.year && item.month && !item.isPreviousYear)
			.sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month))
			.map((item) => {
				const spendValue = extractSpendValue(item);
				return {
					monthLabel: `${MONTH_ABBR[item.month - 1]} '${String(item.year).slice(2)}`,
					monthFull: `${MONTH_FULL[item.month - 1]} ${item.year}`,
					monthKey: `${item.year}-${item.month}`,
					year: String(item.year),
					spendValue,
				};
			});
	}, [monthlySpend]);

	const spendSummary = useMemo(() => {
		if (!monthlySpend || monthlySpend.length === 0) {
			return { totalSpend: 0, percentChange: null };
		}

		let latestTotal = 0;
		let previousTotal = 0;

		monthlySpend.forEach((item) => {
			if (!item || !item.year || !item.month) return;
			const spendValue = extractSpendValue(item);

			if (item.isPreviousYear) {
				previousTotal += spendValue;
			} else {
				latestTotal += spendValue;
			}
		});

		const percentChange =
			previousTotal > 0
				? roundToOneDecimalPlace(((latestTotal - previousTotal) * 100) / previousTotal)
				: null;

		return {
			totalSpend: latestTotal,
			percentChange,
		};
	}, [monthlySpend]);

	const supplierPerformanceAnalytics = useMemo(() => {
		if (!supplierPerformance || supplierPerformance.length === 0) return [];

		return supplierPerformance
			.map((row) => {
				const avgResolutionTimeSeconds = parseSafeFloat(row && row.avgResolutionTimeSeconds, null);
				const avgResponseTimeSeconds = parseSafeFloat(row && row.avgResponseTimeSeconds, null);
				return {
					...row,
					supplierFacilityId: row.supplierFacilityId,
					supplierFacilityName: formatSupplierName(row),
					woCount: row.woCount || 0,
					avgResolutionTimeSeconds: avgResolutionTimeSeconds,
					avgResolutionMinutes: toMinutes(avgResolutionTimeSeconds),
					avgResponseTimeSeconds: avgResponseTimeSeconds === null ? 0 : avgResponseTimeSeconds,
					avgResponseTimeMinutes: toMinutes(
						avgResponseTimeSeconds === null ? 0 : avgResponseTimeSeconds
					),
					isInternalTeam: !!row.isInternalTeam,
					firstTimeFixRate: row.firstTimeFixRate || 0,
				};
			})
			.filter(
				(row) => row.avgResolutionTimeSeconds !== null && row.avgResolutionTimeSeconds !== undefined
			);
	}, [supplierPerformance]);

	const filteredSupplierPerformance = useMemo(() => {
		const filtered = supplierPerformanceAnalytics.filter(
			(row) =>
				(minWoCount <= 0 || row.woCount >= minWoCount) &&
				row.avgResponseTimeSeconds > 0 &&
				row.avgResolutionTimeSeconds > 0
		);
		if (filtered.length === 0) return [];

		// Compute percentile ranks (0 = fastest/best, 1 = slowest/worst)
		const responseValues = filtered.map((row) => row.avgResponseTimeSeconds).sort((a, b) => a - b);
		const resolutionValues = filtered
			.map((row) => row.avgResolutionTimeSeconds)
			.sort((a, b) => a - b);

		const percentileRank = (sortedValues, value) => {
			if (sortedValues.length === 0) return 0;
			let belowCount = 0;
			let equalCount = 0;
			for (let i = 0; i < sortedValues.length; i++) {
				if (sortedValues[i] < value) {
					belowCount++;
				} else if (sortedValues[i] === value) {
					equalCount++;
				}
			}
			return (belowCount + 0.5 * equalCount) / sortedValues.length;
		};

		// Weighted score: 60% response time, 40% resolution time
		// Lower score = better performer
		const scored = filtered.map((row, index) => {
			const responsePctRank = percentileRank(responseValues, row.avgResponseTimeSeconds);
			const resolutionPctRank = percentileRank(resolutionValues, row.avgResolutionTimeSeconds);
			const score = 0.6 * responsePctRank + 0.4 * resolutionPctRank;
			const id = row.supplierFacilityId !== undefined ? row.supplierFacilityId : index;
			return { id, score };
		});

		const sorted = scored.slice().sort((a, b) => a.score - b.score);
		const groupSize = Math.max(1, Math.min(3, Math.floor(sorted.length / 3)));
		const topCount = groupSize;
		const bottomCount = groupSize;
		const performerGroupById = new Map();

		for (let i = 0; i < topCount; i++) {
			performerGroupById.set(sorted[i].id, PERFORMER_GROUP.TOP);
		}
		for (let i = sorted.length - 1, count = 0; i >= 0 && count < bottomCount; i--) {
			if (!performerGroupById.has(sorted[i].id)) {
				performerGroupById.set(sorted[i].id, PERFORMER_GROUP.BOTTOM);
				count++;
			}
		}

		return filtered.map((row, index) => {
			const id = row.supplierFacilityId !== undefined ? row.supplierFacilityId : index;
			return {
				...row,
				performerGroup: performerGroupById.get(id) || PERFORMER_GROUP.TYPICAL,
			};
		});
	}, [supplierPerformanceAnalytics, minWoCount]);

	const previousFilteredSupplierPerformance = useMemo(() => {
		const raw = (comparisonData && comparisonData[COMPARISON_CHART_KEYS.WO_PERFORMANCE]) || [];
		if (!raw || raw.length === 0) return [];
		return raw
			.map((row) => {
				const avgResolutionTimeSeconds = parseSafeFloat(row && row.avgResolutionTimeSeconds, null);
				const avgResponseTimeSeconds = parseSafeFloat(row && row.avgResponseTimeSeconds, null);
				return {
					...row,
					supplierFacilityId: row.supplierFacilityId,
					supplierFacilityName: formatSupplierName(row),
					woCount: row.woCount || 0,
					avgResolutionTimeSeconds,
					avgResolutionMinutes: toMinutes(avgResolutionTimeSeconds),
					avgResponseTimeSeconds: avgResponseTimeSeconds === null ? 0 : avgResponseTimeSeconds,
					avgResponseTimeMinutes: toMinutes(
						avgResponseTimeSeconds === null ? 0 : avgResponseTimeSeconds
					),
					isInternalTeam: !!row.isInternalTeam,
				};
			})
			.filter(
				(row) =>
					row.avgResolutionTimeSeconds !== null &&
					row.avgResolutionTimeSeconds !== undefined &&
					row.avgResponseTimeSeconds > 0 &&
					row.avgResolutionTimeSeconds > 0 &&
					(minWoCount <= 0 || row.woCount >= minWoCount)
			);
	}, [comparisonData, minWoCount]);

	const exportSpendCsv = useCallback(() => {
		if (spendChartData.length === 0) return;
		const priorityLabel = selectedPriorityCard ? selectedPriorityCard.woPriorityName : 'All';
		const spendComparisonState = chartComparisons && chartComparisons[COMPARISON_CHART_KEYS.SPEND];
		const hasComparison =
			!!spendComparisonState &&
			spendComparisonState.mode !== COMPARISON_MODE.NONE &&
			previousSpendChartData.length > 0;
		const baseHeaders = ['Month', 'Year', `Spend (${currency.id})`];
		const headers = hasComparison ? [...baseHeaders, 'Historical Data'] : baseHeaders;
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'Spend_' + priorityLabel.replace(/\s+/g, '_'),
			headers,
		});
		const buildRow = (row, isHistorical) => {
			const base = {
				month: row.monthLabel,
				year: row.year,
				spend: row.spendValue,
			};
			return hasComparison ? { ...base, historicalData: isHistorical ? 'Yes' : 'No' } : base;
		};
		const currentRows = spendChartData.map((row) => buildRow(row, false));
		const previousRows = hasComparison
			? previousSpendChartData.map((row) => buildRow(row, true))
			: [];
		csvExporter.generateCsv([...previousRows, ...currentRows]);
	}, [spendChartData, previousSpendChartData, selectedPriorityCard, currency, chartComparisons]);

	const exportPrioritySummaryCsv = useCallback(() => {
		if (tableDataSource.length === 0) return;
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'Life_Cycle_Analytics_Priority_Summary',
			headers: [
				'Priority',
				'Completed Work Orders',
				'Avg Approval Time (seconds)',
				'Avg Approval Time',
				'Avg Response Time (seconds)',
				'Avg Response Time',
				'Avg Resolution Time (seconds)',
				'Avg Resolution Time',
				'First Time Fix Rate (%)',
			],
		});
		const data = tableDataSource.map((row) => {
			return {
				priority: row.woPriorityName,
				completedWoCount: row.completedWoCount,
				avgApprovalTimeSeconds: row.avgApprovalTimeSeconds,
				avgApprovalTimeFormatted: formatDurationShort(row.avgApprovalTimeSeconds),
				avgResponseTimeSeconds: row.avgResponseTimeSeconds,
				avgResponseTimeFormatted: formatDurationShort(row.avgResponseTimeSeconds),
				avgResolutionTimeSeconds: row.avgResolutionTimeSeconds,
				avgResolutionTimeFormatted: formatDurationShort(row.avgResolutionTimeSeconds),
				firstTimeFixRate: roundToOneDecimalPlace(row.firstTimeFixRate || 0),
			};
		});
		csvExporter.generateCsv(data);
	}, [tableDataSource]);

	const exportSupplierPerformanceCsv = useCallback(() => {
		const dataToExport = filteredSupplierPerformance;
		if (dataToExport.length === 0) return;
		const priorityLabel = selectedPriorityCard ? selectedPriorityCard.woPriorityName : 'All';
		const woComparisonState =
			chartComparisons && chartComparisons[COMPARISON_CHART_KEYS.WO_PERFORMANCE];
		const hasComparison =
			!!woComparisonState &&
			woComparisonState.mode !== COMPARISON_MODE.NONE &&
			previousFilteredSupplierPerformance.length > 0;
		const baseHeaders = [
			'Supplier',
			'WOs',
			'Avg Response Time (seconds)',
			'Avg Response Time',
			'Avg Resolution Time (seconds)',
			'Avg Resolution Time',
			'Performer Group',
		];
		const headers = hasComparison ? [...baseHeaders, 'Historical Data'] : baseHeaders;
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'WO_Performance_' + priorityLabel.replace(/\s+/g, '_'),
			headers,
		});
		const buildRow = (row, isHistorical) => {
			const base = {
				supplier: row.supplierFacilityName,
				woCount: row.woCount,
				avgResponseTimeSeconds: row.avgResponseTimeSeconds,
				avgResponseTimeFormatted: formatDurationShort(row.avgResponseTimeSeconds),
				avgResolutionTimeSeconds: row.avgResolutionTimeSeconds,
				avgResolutionTimeFormatted: formatDurationShort(row.avgResolutionTimeSeconds),
				// previousFilteredSupplierPerformance rows have no performer-group classification
				// (ranking is only computed for the current period), so previous rows fall back to ''.
				performerGroup: row.performerGroup || '',
			};
			return hasComparison ? { ...base, historicalData: isHistorical ? 'Yes' : 'No' } : base;
		};
		const currentRows = dataToExport.map((row) => buildRow(row, false));
		const previousRows = hasComparison
			? previousFilteredSupplierPerformance.map((row) => buildRow(row, true))
			: [];
		csvExporter.generateCsv([...previousRows, ...currentRows]);
	}, [
		filteredSupplierPerformance,
		previousFilteredSupplierPerformance,
		selectedPriorityCard,
		chartComparisons,
	]);
	const handleSpendIntervalClick = useCallback(
		(ev) => {
			const data = nullSafeGet('data.data', ev);
			const monthKey = data && data.monthKey;
			if (monthKey) {
				const startDate = moment(monthKey, 'YYYY-M').startOf('month').format('YYYY-MM-DD');
				const endDate = moment(monthKey, 'YYYY-M').endOf('month').format('YYYY-MM-DD');
				history.push(
					'/' +
						userType +
						'/analytics/spend/overview/problemTypes?startDate=' +
						startDate +
						'&endDate=' +
						endDate +
						'&sortBy=spend&sortOrder=desc'
				);
			}
		},
		[history, userType]
	);

	const formatSpendAxisLabel = useCallback((val) => currency.format(val, 0), [currency]);

	const buildSpendTooltip = useCallback(
		(monthFull, spendValue, monthKey) => {
			const startDate = moment(monthKey, 'YYYY-M').startOf('month').format('YYYY-MM-DD');
			const endDate = moment(monthKey, 'YYYY-M').endOf('month').format('YYYY-MM-DD');
			const detailUrl =
				'/' +
				userType +
				'/analytics/spend/overview/problemTypes?startDate=' +
				startDate +
				'&endDate=' +
				endDate +
				'&sortBy=spend&sortOrder=desc';
			return {
				name: monthFull,
				value: buildSpendTooltipHtml(
					monthFull,
					currency.format(spendValue, 0),
					detailUrl,
					chartColor
				),
			};
		},
		[userType, currency, chartColor]
	);

	const handleWoPerformancePointClick = useCallback(
		(ev) => {
			const data = nullSafeGet('data.data', ev);
			navigateToSupplierDetail(data && data.supplierFacilityId);
		},
		[navigateToSupplierDetail]
	);

	const formatDurationAxisLabel = useCallback((val) => {
		const minutes = parseFloat(String(val));
		if (isNaN(minutes)) return `${val}`;
		return formatDurationShort(minutes * 60);
	}, []);

	const buildPerformanceTooltip = useCallback(
		(name, woCount, responseSeconds, resolutionSeconds) => ({
			name: name,
			value: buildSupplierTooltipHtml(name, woCount, responseSeconds, resolutionSeconds),
		}),
		[]
	);

	const formatPerformerShape = useCallback((performerGroup, isInternalTeam) => {
		if (isInternalTeam) return PERFORMER_SHAPE.STAR;
		if (performerGroup === PERFORMER_GROUP.TOP) return PERFORMER_SHAPE.STAR;
		if (performerGroup === PERFORMER_GROUP.BOTTOM) return PERFORMER_SHAPE.CROSS;
		return PERFORMER_SHAPE.CIRCLE;
	}, []);

	const formatPerformerSize = useCallback((performerGroup, isInternalTeam) => {
		if (isInternalTeam || performerGroup === PERFORMER_GROUP.TOP) return 6;
		return 5;
	}, []);

	const formatPerformerStyle = useCallback(
		(performerGroup, isInternalTeam) => {
			const color = chartColor;
			if (performerGroup === PERFORMER_GROUP.TOP) {
				return { fill: color, stroke: color };
			}
			if (performerGroup === PERFORMER_GROUP.TYPICAL) {
				return {
					lineWidth: 1.5,
					fill: 'transparent',
					stroke: color,
				};
			}
			if (performerGroup === PERFORMER_GROUP.BOTTOM) {
				return {
					lineWidth: 1.5,
					fill: null,
					stroke: color,
				};
			}
			return {
				lineWidth: 1.5,
				fill: color,
				stroke: color,
			};
		},
		[chartColor]
	);

	const renderWoCount = useCallback((value) => value || 0, []);
	const renderDurationColumn = useCallback((value) => formatDurationShort(value || 0), []);
	const renderFirstTimeFixRate = useCallback((value) => {
		const num = parseFloat(String(value));
		return isNaN(num) ? '-' : `${roundToOneDecimalPlace(num)}%`;
	}, []);
	const sortWoCount = useCallback((a, b) => a.woCount - b.woCount, []);
	const sortAvgResponseTime = useCallback(
		(a, b) => a.avgResponseTimeSeconds - b.avgResponseTimeSeconds,
		[]
	);
	const sortAvgResolutionTime = useCallback(
		(a, b) => a.avgResolutionTimeSeconds - b.avgResolutionTimeSeconds,
		[]
	);
	const sortFirstTimeFixRate = useCallback(
		(a, b) => parseFloat(a.firstTimeFixRate || 0) - parseFloat(b.firstTimeFixRate || 0),
		[]
	);

	const handleRowClick = useCallback(
		(record) => ({
			onClick: () => handleArrowClick(record.woPriorityId),
			style: { cursor: 'pointer' },
		}),
		[handleArrowClick]
	);

	const renderSupplierLink = useCallback(
		(text, record) => {
			const supplierFacilityId = record.supplierFacilityId;
			if (!supplierFacilityId) return text;
			return (
				<div
					className="cursor-pointer text-inherit no-underline hover:underline"
					onClick={(e) => {
						e.preventDefault();
						navigateToSupplierDetail(supplierFacilityId);
					}}
				>
					{text}
				</div>
			);
		},
		[navigateToSupplierDetail]
	);

	const supplierTableColumns = useMemo(
		() =>
			buildLifeCycleSupplierTableColumns({
				filteredSupplierPerformanceLength: filteredSupplierPerformance.length,
				renderSupplierLink,
				renderWoCount,
				sortWoCount,
				sortAvgResponseTime,
				renderDurationColumn,
				sortAvgResolutionTime,
				sortFirstTimeFixRate,
				renderFirstTimeFixRate,
			}),
		[
			filteredSupplierPerformance.length,
			renderSupplierLink,
			renderWoCount,
			sortWoCount,
			sortAvgResponseTime,
			renderDurationColumn,
			sortAvgResolutionTime,
			sortFirstTimeFixRate,
			renderFirstTimeFixRate,
		]
	);

	const showCharts = totalCompletedWorkOrders > 0 && viewMode !== VIEW_MODE.ALL;
	const isDetailView = viewMode !== VIEW_MODE.ALL;

	return {
		companyConfig,
		header: {
			title: isDetailView ? 'Life Cycle Analytics: Details' : 'Life Cycle Analytics',
			isDetailView,
		},
		dashboard: {
			spinning: loading || chartLoading || prioritiesLoading,
			error,
			prioritySummaryProps: {
				loading,
				totalCompletedWorkOrders,
				prioritySummaryCards,
				tableDataSource,
				priorityTableColumns,
				viewMode,
				selectedPriorityId,
				hasActiveFilters,
				activeFilters,
				problemTypeNames,
				handleRowClick,
				handleCardClick,
				openFilterModal,
				exportPrioritySummaryCsv,
				removeProblemTypeFilter,
				removeIsPMFilter,
				handleRemoveCreatedAtFilter,
				handleRemoveCompletedAtFilter,
			},
			chartsAndPerformanceProps: {
				showCharts,
				metricChartPropsList,
				spendChartProps: {
					chartColor,
					currency,
					spendChartData,
					previousPeriodChartData: previousSpendChartData,
					spendSummary,
					selectedPriorityCard,
					formatSpendAxisLabel,
					buildSpendTooltip,
					handleSpendIntervalClick,
					exportSpendCsv,
					comparisonState:
						(chartComparisons && chartComparisons[COMPARISON_CHART_KEYS.SPEND]) || null,
					onComparisonChange: setComparisonForChart
						? (next: any) => setComparisonForChart(COMPARISON_CHART_KEYS.SPEND, next)
						: undefined,
					selectedRange: hasFullCompletedAtRange
						? [activeFilters.completedAt[0], activeFilters.completedAt[1]]
						: null,
					comparisonRange:
						hasFullCompletedAtRange && chartComparisons
							? resolveComparisonRange(
									activeFilters.completedAt[0],
									activeFilters.completedAt[1],
									chartComparisons[COMPARISON_CHART_KEYS.SPEND]
							  )
							: null,
					comparisonLoading: !!(
						comparisonLoading && comparisonLoading[COMPARISON_CHART_KEYS.SPEND]
					),
					comparisonDisabled: !hasFullCompletedAtRange,
					comparisonDisabledReason: !hasFullCompletedAtRange
						? comparisonDisabledReasonText
						: undefined,
				},
				woPerformanceProps: {
					chartColor,
					filteredSupplierPerformance,
					previousSupplierPerformance: previousFilteredSupplierPerformance,
					supplierTableColumns,
					selectedPriorityCard,
					minWoCount,
					setMinWoCount,
					formatDurationAxisLabel,
					formatPerformerShape,
					formatPerformerSize,
					formatPerformerStyle,
					buildPerformanceTooltip,
					handleWoPerformancePointClick,
					exportSupplierPerformanceCsv,
					comparisonState:
						(chartComparisons && chartComparisons[COMPARISON_CHART_KEYS.WO_PERFORMANCE]) || null,
					onComparisonChange: setComparisonForChart
						? (next: any) => setComparisonForChart(COMPARISON_CHART_KEYS.WO_PERFORMANCE, next)
						: undefined,
					selectedRange: hasFullCompletedAtRange
						? [activeFilters.completedAt[0], activeFilters.completedAt[1]]
						: null,
					comparisonRange:
						hasFullCompletedAtRange && chartComparisons
							? resolveComparisonRange(
									activeFilters.completedAt[0],
									activeFilters.completedAt[1],
									chartComparisons[COMPARISON_CHART_KEYS.WO_PERFORMANCE]
							  )
							: null,
					comparisonLoading: !!(
						comparisonLoading && comparisonLoading[COMPARISON_CHART_KEYS.WO_PERFORMANCE]
					),
					comparisonDisabled: !hasFullCompletedAtRange,
					comparisonDisabledReason: !hasFullCompletedAtRange
						? comparisonDisabledReasonText
						: undefined,
				},
			},
		},
		filterDrawer: {
			visible: filterModalVisible,
			draftFilters,
			problemTypes,
			createdAtPickerDates,
			completedAtPickerDates,
			fetchProblemTypes,
			handleProblemTypeChange,
			handleCreatedAtCalendarChange,
			handleCreatedAtChange,
			handleCreatedAtOpenChange,
			handleCompletedAtCalendarChange,
			handleCompletedAtChange,
			handleCompletedAtOpenChange,
			handleIsPMChange,
			applyFilters,
			cancelFilters,
		},
	};
}
